import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Space, Progress, Upload, Button, Alert, message } from 'antd';
import {
  CloseOutlined,
  DownOutlined,
  PaperClipOutlined,
  FolderOpenOutlined,
  FileSearchOutlined,
  UpOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import type { UploadFile, UploadProps } from 'antd/lib/upload/interface';
import { formaFileSize, getEnterpriseId, getUUID, isRequestError } from '@/utils/utils';
import { useUpload } from '@/hooks';
import _, { groupBy } from 'lodash';
import styles from './index.module.less';
import type { RcFile } from 'antd/lib/upload/interface';
import { isAllowUpload, allowSuffix, isGmodelerFile } from './check';
import jszip from 'jszip';

interface IFile extends UploadFile {
  md5?: string;
  extra: any;
  pid: React.Key;
  batchId: string;
  webkitRelativePath: string;
  isDirectory?: boolean;
  children?: any[];
  dirId?: React.Key;
  [key: string]: any;
}

interface UploadListProps {
  appName: string;
  uploadUrl: string;
  directoryId?: React.Key;
  onUploadSuccess: (files: RcFile[], directoryId?: React.Key) => Promise<any> | void;
  validate: (file: RcFile, fileList: RcFile[]) => boolean | Promise<boolean>;
  selectFile?: (directoryId: React.Key) => void;
  fileFields?: string[]; // 内部可选值有: storageId
  [uploadProps: string]: any;
  isGmodeler?: boolean; //是否正向
}

const fileMap: any = {};
let flag = false;
// 记录xhr
const xhrs: Record<string, XMLHttpRequest | null> = {};

// 并发池
const MAXIMUM = 5;
let poolCount = 0;
const pool: IFile[] = [];

let tempFiles: any = [];
let tempUploads: IFile[] = [];
const UploadList: React.FC<UploadListProps> = forwardRef((props, ref) => {
  const {
    appName,
    uploadUrl,
    directoryId,
    onUploadSuccess,
    selectFile,
    fileFields,
    validate,
    isGmodeler,
    ...restProps
  } = props;

  const fileRef = useRef({} as HTMLDivElement);
  const folderRef = useRef({} as HTMLDivElement);

  const [hide, setHide] = useState(false);
  const [uploads, setUploads] = useState<IFile[]>([]);
  const [show, setShow] = useState(false);

  // 上传相关hooks
  const { uploadRequest } = useUpload();

  useEffect(() => {
    if (!show) {
      setUploads([]);
    }
  }, [show]);

  const errorHandle = (fileId: string, errorMessage?: string) => {
    setUploads((prevState) => {
      return prevState.map((item: any) => {
        if (item.uid === fileId) {
          item.error = errorMessage || '上传失败。发生未知错误，请重新上传';
        }
        if (item.children && item.uid === fileId) {
          item.error = errorMessage || '上传失败。发生未知错误，请重新上传';
        }
        return item;
      });
    });
  };

  const getFile = (file: IFile): any => {
    const fileName = file.name.slice(0, file.name.lastIndexOf('.'));
    const suffix = file.name.split('.').pop() || '';
    const data: any = {
      batchId: file.batchId,
      enterpriseId: getEnterpriseId() || '',
      fileName: fileName,
      fileSize: file?.size || 0,
      fileSuffix: suffix,
      storageFileId: file.extra,
      storageId: file.storageId,
      pid: file.pid,
      path: file?.webkitRelativePath,
      fileType: file.fileType,
      folderName: file.folderName,
      folderPath: file.folderPath,
      name: fileName,
      componentId: file.componentId,
      familyType: file.familyType,
      referenceFaceVertical: file.referenceFaceVertical,
      referenceFaceHorizontal: file.referenceFaceHorizontal,
      referencePointHorizontal: file.referencePointHorizontal,
      referencePointVertical: file.referencePointVertical,
      targetHost: file.targetHost,
      levelHost: file.levelHost,
      suffix,
      uid: file?.uid || `new-file-${getUUID()}`,
      isAllowSetting: file.isAllowSetting,
      isHasReference: file.isHasReference,
    };

    (fileFields || []).map((field: string) => {
      data[field] = file[field];
    });

    return data;
  };

  const completeUpload = async (files: any, fileId: string) => {
    try {
      const res = (await onUploadSuccess(files, directoryId)) || {};
      if (isRequestError(res)) {
        errorHandle(fileId, res?.message);
      } else {
        const { dirId } = res || {};
        setUploads((prevState) => {
          return prevState.map((item: any) => {
            if (item.uid === fileId) {
              item.status = 'success';
              if (item.isDirectory && dirId) item.dirId = dirId;
            }
            return item;
          });
        });
      }
    } catch (e) {
      console.log(e);
      errorHandle(fileId);
    }
  };

  const saveFile = (file: IFile) => {
    if (file.batchId) {
      let directory: IFile | undefined;
      if (file.isDirectory) {
        directory = file;
      } else {
        directory = tempUploads.find((upload) => upload.uid === file.batchId);
      }
      const isDone: boolean = (directory?.children || []).every(
        (upload) => upload.status === 'done',
      );
      if (isDone) {
        const files: any = (directory?.children || []).map((child) => getFile(child));
        completeUpload(files, directory?.uid || '');
      }
    } else {
      completeUpload([getFile(file)], file.uid);
    }
  };

  const uploadFile = (file: IFile): Promise<void> => {
    const fileSuffix = (file?.name?.split('.')?.pop() ?? 'rvt').toLowerCase();
    return new Promise((resolve, reject) => {
      poolCount++;
      uploadRequest({
        appName: 'boss',
        url: uploadUrl,
        fileStream: file?.fileStream || file,
        enterpriseId: getEnterpriseId(),
        fileName: `${file.name.slice(0, file.name.lastIndexOf('.'))}.${fileSuffix}`,
        fileSuffix,
        fullPath: '/test/',
        // fileData: originFileObj,
        fileSize: file?.size,
        fileType: 'document',
        onPreload: (xhr: XMLHttpRequest, storage: any) => {
          file.extra = storage.storageFileId;
          file.storageId = storage.storageId;

          xhrs[file.uid] = xhr;
        },
        onProgress: (res: any) => {
          setUploads((prevState) => {
            return prevState.map((item: any) => {
              if (item.uid === file.uid) {
                item.percent = res.percent;
                item.status = 'uploading';
              }
              if (item.children && item.uid === file.batchId) {
                let countSize = 0;
                item.children = (item.children || []).map((child: any) => {
                  if (child.uid === file.uid) {
                    child.percent = res.percent;
                    child.status = 'uploading';
                  }

                  countSize += child.percent;
                  return child;
                });
                item.percent = countSize / item.count;
                console.log('item.percent', item.percent);
              }
              return item;
            });
          });
        },
        onSuccess: () => {
          setUploads((prevState) => {
            return prevState.map((item: any) => {
              if (item.uid === file.uid) {
                item.percent = 100;
                item.status = 'done';
              }

              if (item.children && item.uid === file.batchId) {
                item.children = (item.children || []).map((child: any) => {
                  if (child.uid === file.uid) {
                    child.percent = 100;
                    child.status = 'done';
                  }

                  return child;
                });
                item.status = item.children.every((child: any) => child.status === 'done')
                  ? 'done'
                  : 'uploading';
              }
              return item;
            });
          });

          xhrs[file.uid] = null;
          saveFile(file);
          resolve();
        },
        onError: (error: any) => {
          const { exists, storage }: { exists: boolean; storage: any } = error;
          setUploads((prevState) => {
            return prevState.map((item: any) => {
              if (item.uid === file.uid) {
                if (exists) {
                  item.percent = 100;
                  item.status = 'done';
                } else {
                  item.error = '上传失败。发生未知错误，请重新上传';
                }
              }

              if (item.children && item.uid === file.batchId) {
                let countSize = 0;
                item.children = (item.children || []).map((child: any) => {
                  if (child.uid === file.uid && exists) {
                    child.percent = 100;
                    child.status = 'done';
                  }

                  countSize += child.percent;
                  return child;
                });

                if (exists) {
                  item.percent = countSize / item.count;
                  console.log('item.percent', item.percent);

                  item.status = item.children.every((child: any) => child.status === 'done')
                    ? 'done'
                    : 'uploading';
                } else {
                  item.error = '上传失败。发生未知错误，请重新上传';
                }
              }
              return item;
            });
          });

          if (exists) {
            file.extra = storage.storageFileId;
            file.storageId = storage.storageId;
            saveFile(file);
            resolve();
          } else {
            reject();
          }
        },
      });
    });
  };

  const uploadFileList = (file: IFile) => {
    if (poolCount >= MAXIMUM) {
      pool.push(file);
      return;
    }

    uploadFile(file).then(() => {
      poolCount--;
      const currentFile = pool.shift();
      if (currentFile) {
        uploadFileList(currentFile);
      }
    });
  };

  const startUpload = async (allFiles: IFile[]) => {
    setUploads((files: IFile[]) => [...allFiles, ...files]);
    setShow(true);
    allFiles.forEach((f) => {
      uploadFileList(f);
    });
  };

  useImperativeHandle(ref, () => {
    return {
      // 选择文件
      selectFile: () => {
        fileRef?.current?.click();
      },
      // 选择目录
      selectDirectory: () => {
        folderRef?.current?.click();
      },
      startUpload,
    };
  });

  // const getJsonName = async (file: any) => {
  //   return new Promise((r) => {
  //     const reader = new FileReader(); // 新建一个FileReader
  //     reader.readAsText(file, 'UTF-8'); // 读取文件
  //     reader.onload = (evt) => {
  //       // 读取完文件之后会回来这里
  //       const fileString: any = evt?.target?.result || ''; // 读取文件内容
  //       const jsonObj = JSON.parse(fileString);
  //       r(jsonObj.name || '');
  //     };
  //   });
  // };

  const getJsonAnother = async (file: any) => {
    return new Promise((r) => {
      const reader = new FileReader();
      reader.readAsText(file, 'UTF-8');
      reader.onload = (evt) => {
        const fileString: any = evt?.target?.result || '';
        const jsonObj = JSON.parse(fileString);
        const elementType = jsonObj?.elementType;
        const isHasElementType = Object.keys(jsonObj)?.includes('elementType');
        const isAllowSetting =
          !isHasElementType || (isHasElementType && (elementType == 316 || elementType == 318));
        const isHasReference =
          Object.keys(jsonObj)?.includes('referencePointHorizontal') ||
          Object.keys(jsonObj)?.includes('referencePointVertical') ||
          Object.keys(jsonObj)?.includes('referenceFaceVertical') ||
          Object.keys(jsonObj)?.includes('referenceFaceHorizontal') ||
          Object.keys(jsonObj)?.includes('targetHost');
        r({
          name: jsonObj.name || '',
          id: jsonObj.guid || '',
          familyType: jsonObj.familyType,
          referencePointHorizontal: jsonObj.referencePointHorizontal || '',
          referencePointVertical: jsonObj.referencePointVertical || '',
          referenceFaceVertical: jsonObj.referenceFaceVertical || '',
          referenceFaceHorizontal: jsonObj.referenceFaceHorizontal || '',
          targetHost: jsonObj.targetHost,
          isAllowSetting,
          isHasReference,
        });
      };
    });
  };
  const getZipFiles = async (file: any, res: any[], rootName?: string) => {
    const zips = await jszip.loadAsync(file, {
      decodeFileName: function (bytes) {
        const iconv = require('iconv-lite');
        return iconv.decode(bytes, 'gbk'); // 按中文编码
      },
    });
    const zfiles: any = zips.files;
    for (let k in zfiles) {
      const cFile = zfiles[k];
      const suffix = (cFile.name?.split('.')?.pop() || '').toLowerCase();
      let fileName = '',
        folderPath = '';
      if (cFile.name?.includes('/') && !cFile.name?.endsWith('/')) {
        fileName = cFile.name?.split('/')?.pop() || '';
        folderPath = cFile.name?.replace(`/${fileName}`, '');
      } else {
        fileName = cFile.name;
        folderPath = file.directoryName;
      }
      if (!cFile.dir && allowSuffix.includes(suffix)) {
        cFile.folderPath = `${rootName ? rootName + '/' : ''}${folderPath}`;
        cFile.fileSuffix = suffix;
        cFile.suffix = suffix;
        cFile.name = fileName;
        cFile.fileType = 'gmodeler';
        if (!cFile.uid) {
          cFile.uid = `new-file-${getUUID()}`;
        }
        const fileBlob = await zips.file(k)?.async('blob');
        if (fileName?.toLowerCase() === 'meta.json') {
          // const name = await getJsonName(fileBlob);
          const {
            name,
            familyType,
            id,
            referenceFaceVertical,
            referenceFaceHorizontal,
            targetHost,
            referencePointHorizontal,
            referencePointVertical,
            isAllowSetting,
            isHasReference,
          } = (await getJsonAnother(fileBlob)) as any;
          const folderName = folderPath.split('/').pop();
          cFile.folderName = name || folderName;
          cFile.componentId = id;
          cFile.familyType = familyType;
          if (isAllowSetting) {
            cFile.referenceFaceVertical = referenceFaceVertical;
            cFile.referenceFaceHorizontal = referenceFaceHorizontal;
            cFile.referencePointHorizontal = referencePointHorizontal;
            cFile.referencePointVertical = referencePointVertical;
            cFile.targetHost = targetHost;
            cFile.isHasReference = isHasReference;
          }
          cFile.isAllowSetting = isAllowSetting;
          if (!cFile.componentId) {
            return { noComponentId: true };
          }
        }
        cFile.fileStream = fileBlob;
        cFile.size = fileBlob?.size;
        res.push(cFile);
      }
      if (cFile.name?.toLowerCase()?.endsWith('.zip')) {
        const fileBlob = await zips.file(k)?.async('blob');
        const rootFolderName = cFile.name?.slice(0, cFile.name?.lastIndexOf('.'));
        await getZipFiles(fileBlob, res, rootFolderName);
      }
    }
  };

  const getUploadFiles = (files: any[], batchId: string) => {
    const groups = groupBy(files, 'folderPath');
    const uploadFiles: any = [];
    for (let k in groups) {
      const group = groups[k];
      const isUpload = isGmodelerFile(group);
      if (isUpload) {
        group?.forEach((g) => {
          g.batchId = batchId;
          uploadFiles.push(g);
        });
      }
    }
    return uploadFiles;
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    showUploadList: false,
    ...restProps,
    beforeUpload: async (file: any, fileList: any) => {
      // console.log(file, fileList, '============before');
      const isUpload = await validate(file, fileList);
      file.folderPath = file.webkitRelativePath
        ? file.webkitRelativePath?.replace('/' + file.name, '')
        : '';
      let res = null;

      const isZip = file.name?.toLowerCase()?.endsWith('.zip');
      // let allZipFiles: any = [];
      if (isGmodeler) {
        // res = await isAllowUpload(fileList, file);
        if (isZip) {
          const zipFiles: any = [];
          const batchId = getUUID();
          file.directoryName = file.name?.slice(0, file.name?.lastIndexOf('.'));
          const res = await getZipFiles(file, zipFiles);
          // const jsonFile = zipFiles?.find();
          if (res && res.noComponentId) {
            message.warn('无法识别构件ID');
            return false;
          }
          tempFiles = getUploadFiles(zipFiles, batchId);
          file.batchId = batchId;
        } else {
          res = await isAllowUpload(fileList, file);
        }
      }
      // 上传文件夹
      if (!flag && file.webkitRelativePath) {
        flag = true;
        const uuid = getUUID();
        fileList.forEach((item: any) => {
          fileMap[`${item.webkitRelativePath}`] = uuid;
        });
      }

      if (!show && isUpload) {
        setShow(true);
      }

      if (file.name?.toLowerCase() === 'meta.json') {
        // const name = await getJsonName(file);
        const folderName = file.folderPath.split('/').pop();
        const {
          name,
          familyType,
          id,
          referenceFaceVertical,
          referenceFaceHorizontal,
          targetHost,
          referencePointVertical,
          referencePointHorizontal,
          isAllowSetting,
          isHasReference,
        } = (await getJsonAnother(file)) as any;
        file.componentId = id;
        file.familyType = familyType;
        file.folderName = name || folderName;
        if (isAllowSetting) {
          file.referenceFaceVertical = referenceFaceVertical;
          file.referenceFaceHorizontal = referenceFaceHorizontal;
          file.referencePointVertical = referencePointVertical;
          file.referencePointHorizontal = referencePointHorizontal;
          file.targetHost = targetHost;
          file.isHasReference = isHasReference;
        }
        file.isAllowSetting = isAllowSetting;
        if (!file.componentId) {
          message.warn('无法识别构件ID');
          return false;
        }
      }
      if (isGmodeler && res?.upload) {
        file.fileType = res.type;
      }
      file.md5 = '';
      file.percent = 0;
      file.status = 'uploading';
      file.pid = directoryId;
      if (fileMap[file.webkitRelativePath]) {
        if (isUpload) {
          file.batchId = fileMap[file.webkitRelativePath];
          if ((isGmodeler && res?.upload) || !isGmodeler) {
            tempFiles.push(file);
          }
        }
        delete fileMap[file.webkitRelativePath];
      }
      // console.log(tempFiles, '==========tempFiles', res);
      // 文件夹批量上传
      if (_.isEmpty(fileMap) || (isZip && isGmodeler)) {
        if (file.batchId) {
          const fileGroup: any = {
            uid: file.batchId,
            isDirectory: true,
            name: file.directoryName || file.webkitRelativePath.split('/')[0],
            percent: 0,
            status: 'uploading',
            pid: directoryId,
            children: [...tempFiles],
            count: tempFiles.length,
          };
          if (fileGroup?.children.length > 0) {
            setUploads((files: IFile[]) => [...files, fileGroup]);
          }
          tempFiles.forEach((f: IFile) => {
            uploadFileList(f);
          });
        }
        tempFiles = [];
        flag = false;
      }

      if (!isUpload) {
        return false;
      }

      if (!file.batchId) {
        setUploads((files: IFile[]) => [...files, file]);
        await uploadFileList(file);
      }

      return false;
    },
  };

  const toggleHide = () => {
    setHide(!hide);
  };

  const handleClose = () => {
    setUploads([]);
    setShow(false);
  };

  // 删除上传的文件
  // const handleAbort = (fileId: string) => {
  //   if (xhrs[fileId]) {
  //     xhrs[fileId]?.abort();
  //     xhrs[fileId] = null;
  //   }
  //
  //   setUploads((prevState) => {
  //     return prevState.filter((item) => item.uid !== fileId);
  //   });
  // };

  // 重新上传
  const reload = async (file: IFile) => {
    file.error = '';
    if (file.status === 'done') {
      saveFile(file);
      return;
    }

    if (file.isDirectory && file.children) {
      file.children.forEach((child) => {
        if (child.status !== 'done') {
          uploadFileList(child);
        }
      });
    } else {
      await uploadFileList(file);
    }
  };

  // 批量重传
  const batchReload = () => {
    uploads.forEach((file) => {
      if (file.error) {
        reload(file);
      }
    });
  };

  // 文件状态渲染
  const getFileIcon = (file: IFile) => {
    if (file.error) {
      return (
        <Space>
          <ReloadOutlined title="重传" className={styles.uploadIcon} onClick={() => reload(file)} />
          {/*<IconFont*/}
          {/*  title="删除"*/}
          {/*  type="icon-a-shanchu"*/}
          {/*  className={styles.uploadIcon}*/}
          {/*  onClick={() => handleAbort(file.uid)}*/}
          {/*/>*/}
        </Space>
      );
    }

    if (file.status === 'success' && selectFile) {
      const fileId = file?.isDirectory ? file?.dirId : directoryId;
      return (
        <FileSearchOutlined
          title="查看文件位置"
          className={styles.uploadIcon}
          onClick={() => selectFile(fileId || '')}
        />
      );
    }

    // return (
    //   <IconFont
    //     title="删除"
    //     type="icon-a-shanchu"
    //     className={styles.uploadIcon}
    //     onClick={() => handleAbort(file.uid)}
    //   />
    // );
    return null;
  };

  useEffect(() => {
    tempUploads = uploads;
  }, [uploads]);

  // 汇总数据
  const summary: {
    doneCount: number;
    isDone: boolean;
    averageProgress: number;
    totalSize: number;
    errorCount: number;
  } = React.useMemo(() => {
    // 完成的数量
    const doneCount = uploads.filter((item) => item.status === 'success')?.length;
    // 是否全部上传完成
    const isDone = uploads.every((item) => item.status === 'success');

    const countProgress = _.reduce(
      uploads,
      (sum, item) => {
        return sum + (item.percent || 0);
      },
      0,
    );
    // 平均进度
    // console.log('uploads', countProgress, uploads);
    const averageProgress = Math.round(countProgress / uploads.length);

    // 总大小
    const size = _.reduce(
      uploads,
      (sum, item) => {
        let count = item.size || 0;
        if (item.children && item.children.length) {
          count = _.reduce(
            item.children,
            (cSum, cItem) => {
              return cSum + (cItem.size || 0);
            },
            0,
          );
        }
        return sum + count;
      },
      0,
    );
    // const totalSize = (size / 1024).toFixed(2);

    const isUploading = uploads.some((item) => item.status === 'uploading' && !item.error);
    const errors = uploads.filter((item) => item.error)?.length;
    const errorCount = isUploading ? 0 : errors;

    return {
      doneCount,
      isDone,
      averageProgress,
      totalSize: size,
      errorCount,
    };
  }, [uploads]);

  const uploaDirectoryProps = {
    ...uploadProps,
    accept: isGmodeler ? '.png,.dwg,.glb,.gmr,.json,.dxf' : uploadProps.accept,
  };

  return (
    <>
      <Upload {...uploadProps} className={styles.hideUploadBtn}>
        <Button
          id="project-upload-file"
          style={{ display: 'none', position: 'absolute' }}
          ref={fileRef}
        >
          上传文件
        </Button>
      </Upload>
      <Upload {...uploaDirectoryProps} directory className={styles.hideUploadBtn}>
        <Button
          id="project-upload-directory"
          style={{ display: 'none', position: 'absolute' }}
          ref={folderRef}
        >
          上传文件夹
        </Button>
      </Upload>

      <div className={classNames(styles.uploadListWrap, { [styles.show]: show })}>
        <div className={styles.header}>
          <Space size={4}>
            {summary.isDone ? (
              <CheckCircleOutlined className={classNames(styles.titleIcon, styles.success)} />
            ) : summary.errorCount ? (
              <ExclamationCircleOutlined className={classNames(styles.titleIcon, styles.warning)} />
            ) : (
              <SyncOutlined className={styles.titleIcon} />
            )}
            <span className={styles.summary}>
              {summary.isDone ? (
                <>
                  全部上传成功 {uploads?.length}/{uploads?.length}
                </>
              ) : summary.errorCount ? (
                <>{summary.errorCount}项上传失败</>
              ) : (
                <>
                  上传中 {summary.doneCount}/{uploads?.length}
                </>
              )}
            </span>
          </Space>

          <Space>
            {hide ? (
              <UpOutlined className={styles.uploadIcon} onClick={toggleHide} />
            ) : (
              <DownOutlined className={styles.uploadIcon} onClick={toggleHide} />
            )}
            <CloseOutlined className={styles.uploadIcon} onClick={handleClose} />
          </Space>
        </div>

        <div
          className={classNames(styles.progressBar, {
            [styles.hidden]: hide || !uploads?.length,
            [styles.hiddenProgress]: summary.isDone || summary.errorCount,
          })}
        >
          {summary.isDone ? (
            <div className={styles.info}>
              上传成功{uploads?.length}项，共{formaFileSize(summary.totalSize, 1)}
            </div>
          ) : summary.errorCount ? (
            <div className={styles.info}>
              上传成功{summary.doneCount}项，失败{summary.errorCount}项
            </div>
          ) : (
            <div className={styles.info}>平均进度：{summary.averageProgress}%</div>
          )}
          {summary.errorCount > 0 && (
            <div onClick={batchReload} className={styles.operation}>
              重试
            </div>
          )}
          <Progress
            percent={summary.isDone || summary.errorCount ? 100 : summary.averageProgress}
            strokeWidth={36}
            showInfo={false}
            strokeColor="var(--patternx-brand-1)"
            strokeLinecap="square"
          />
        </div>

        <div className={classNames(styles.list, { [styles.hidden]: hide || !uploads?.length })}>
          {uploads.map((file) => (
            <div key={file.uid} className={styles.listItem}>
              <div className={styles.innerItem}>
                <div
                  className={classNames(styles.titleBlock, {
                    [styles.success]: file.status === 'success',
                  })}
                >
                  {file.isDirectory ? (
                    <FolderOpenOutlined className={styles.uploadIcon} />
                  ) : (
                    <PaperClipOutlined className={styles.uploadIcon} />
                  )}
                  <span className={styles.title} title={file.name}>
                    {file.name}
                  </span>
                </div>
                {getFileIcon(file)}
              </div>

              {file.error ? (
                <>
                  <Progress percent={100} strokeWidth={5} status="exception" showInfo={false} />
                  <Alert message={file.error} type="error" showIcon />
                </>
              ) : (
                file.status !== 'success' && (
                  <Progress percent={file.percent} strokeWidth={5} showInfo={false} />
                )
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
});

export default UploadList;
